import get from 'lodash.get';
import { isEqual, isArray } from 'lodash';
import { questionName } from './lib';
import { log } from '../../../utils/log';

export const CRITERIA_TYPE_QUESTION = 'question';
export const CRITERIA_TYPE_CONTEXT = 'context';

export const CONDITION_REQUIRED = 'required';
export const CONDITION_HIDDEN = 'hidden';
export const CONDITION_VISIBLE = 'visible';

/**
 * Evaluate Condition
 * @param {Object} condition Contains info needed to evaluate condition
 * @param {Mixed} value String, Array, Object, Integer value from Form
 * @return {Boolean}
 */
const evaluate = (condition, value) => {
  __DEVELOPMENT__ &&
    log(
      '[FormRenderer:CONDITION]',
      condition.criteria.name || condition.criteria.path,
      `${condition.condition}=${isEqual(condition.criteria.value, value)}`
    );

  const conditionValue = condition.criteria.value;
  if (isArray(conditionValue) && isArray(value)) {
    return value.includes(conditionValue[0]);
  }

  return isEqual(conditionValue, value);
};

/**
 * @param {Object} conditions Collection of Form Conditions
 * @param {String} criteriaType "question"|"context"
 * @return {Object} conditions Collection of Form Conditions
 */
export const getConditions = (conditions = [], criteriaType) =>
  conditions.filter((condition) => condition.criteria.type === criteriaType);

/**
 * @param {Object} conditions Collection of Form Conditions
 * @return {Object} conditions Collection of Form Conditions
 */
export const getContextConditions = (conditions) =>
  getConditions(conditions, CRITERIA_TYPE_CONTEXT);

/**
 * @param {Object} conditions Collection of Form Conditions
 * @return {Object} conditions Collection of Form Conditions
 */
export const getQuestionConditions = (conditions) =>
  getConditions(conditions, CRITERIA_TYPE_QUESTION);

export const evaluateContextConditions = ({ context, question }) =>
  getContextConditions(question.config.conditions).reduce(
    (results, condition) => {
      results[condition.condition] = evaluate(
        condition,
        get(context, condition.criteria.path)
      );
      if (condition.condition === CONDITION_VISIBLE) {
        if (results[CONDITION_VISIBLE] === false) {
          results[CONDITION_HIDDEN] = true;
        }
        if (results[CONDITION_VISIBLE] === true) {
          results[CONDITION_HIDDEN] = false;
        }
        delete results[CONDITION_VISIBLE];
      }

      return results;
    },
    {
      [CONDITION_REQUIRED]: question.config.required,
      [CONDITION_HIDDEN]: question.config.hidden,
      [CONDITION_VISIBLE]: null,
    }
  );

export const evaluateQuestionConditions = ({ values, conditions }) =>
  Object.keys(conditions).reduce(
    (results, name) => {
      conditions[name].forEach((questionCondition) => {
        results[questionCondition.condition] =
          results[questionCondition.condition] || {};
        results[questionCondition.condition][name] =
          results[questionCondition.condition][name] || [];
        results[questionCondition.condition][name].push(
          evaluate(
            questionCondition,
            values[questionName(questionCondition.criteria)]
          )
        );
      });

      Object.keys(results).forEach((type) => {
        Object.keys(results[type]).forEach((name) => {
          // Evaluate multiple conditions as "OR"
          if (Array.isArray(results[type][name])) {
            results[type][name] = results[type][name].some((val) => val);
          }
          // Yuck:
          // Resolve potential conditonial conflicts by
          // inverting `visible` conditions to `hidden`
          // Form structure only knows about :hidden and :required
          if (type === CONDITION_VISIBLE) {
            results[CONDITION_HIDDEN][name] = !results[CONDITION_VISIBLE][name];
            delete results[CONDITION_VISIBLE][name];
          }
        });
      });

      delete results[CONDITION_VISIBLE];

      return results;
    },
    {
      [CONDITION_REQUIRED]: {},
      [CONDITION_HIDDEN]: {},
      [CONDITION_VISIBLE]: {},
    }
  );
